/// <reference types="vite/client" />
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import * as React from "react";
import type { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/tanstackstart-react";
import { isSentryEnabledClient } from "~/lib/sentry/enabled-client";
import { DefaultCatchBoundary } from "~/components/DefaultCatchBoundary";
import { NotFound } from "~/components/NotFound";
import appCss from "~/styles/app.css?url";
import { getBaseUrl } from "~/lib/utils";
import { seo } from "~/utils/seo";
import { TopPanel } from "~/components/Layout/TopPanel";
import { Toaster } from "~/components/ui/sonner";
import { ScrollToTopButton } from "~/components/ScrollToTopButton";
import { useAuthentication } from "~/lib/better-auth/client";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Switch } from "~/components/ui/switch";
import {
  GTM_ID,
  isGtmEnabled,
  loadGtmScript,
  pushToDataLayer,
  readAnalyticsConsent,
  saveAnalyticsConsent,
  type AnalyticsConsent,
} from "~/lib/analytics/gtm";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        name: "color-scheme",
        content: "light",
      },
      {
        name: "theme-color",
        content: "#000000",
      },
      ...seo({
        title: "Locationguide24",
        description: `Locationguide24 ► Eventlocation, Hochzeitslocation, Tagungsraum, Tagungshotel in München, Berlin, Hamburg, Wien, Köln, Frankfurt, Stuttgart, Düsseldorf`,
        url: `${getBaseUrl()}/de`,
        siteName: "Locationguide24",
        locale: "de_DE",
      }),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // {
      //   rel: "apple-touch-icon",
      //   sizes: "180x180",
      //   href: "/apple-touch-icon.png",
      // },
      // {
      //   rel: "icon",
      //   type: "image/png",
      //   sizes: "32x32",
      //   href: "/favicon-32x32.png",
      // },
      // {
      //   rel: "icon",
      //   type: "image/png",
      //   sizes: "16x16",
      //   href: "/favicon-16x16.png",
      // },
      { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
      { rel: "icon", href: "/favicon.ico" },
    ],
  }),
  errorComponent: (props) => {
    // Don't catch NotFoundError - let it be handled by notFoundComponent
    const isNotFoundError = React.useMemo(() => {
      const error = props.error;
      if (!error) return false;

      // Check if it's a Response object with 404 status
      if (error instanceof Response && error.status === 404) {
        return true;
      }

      if (typeof error !== "object") return false;

      // Check for NotFoundError by name
      if (
        error.name === "NotFoundError" ||
        error.constructor?.name === "NotFoundError"
      ) {
        return true;
      }

      // Check for 404 status code
      if ("status" in error && error.status === 404) {
        return true;
      }

      // Check for statusCode property
      if ("statusCode" in error && error.statusCode === 404) {
        return true;
      }

      // Check error message - TanStack Start serializes notFound() as JSON in the message
      if (error.message && typeof error.message === "string") {
        // Try to parse as JSON (TanStack Start serializes notFound() as {"isNotFound":true})
        try {
          const parsed = JSON.parse(error.message);
          if (
            parsed &&
            typeof parsed === "object" &&
            parsed.isNotFound === true
          ) {
            return true;
          }
        } catch {
          // Not JSON, check for text patterns
        }

        // Check for text patterns
        const msg = error.message.toLowerCase();
        if (msg.includes("not found") || msg.includes("404")) {
          return true;
        }
      }

      return false;
    }, [props.error]);

    // If it's a not-found error, re-throw it so it's handled by notFoundComponent
    // We can't actually re-throw in a component, so we'll render NotFound directly
    if (isNotFoundError) {
      return (
        <RootDocument>
          <NotFound />
        </RootDocument>
      );
    }

    // Capture error in Sentry (additional capture at route level)
    // Only capture in production/staging, not during development
    // Skip expected authentication errors to avoid spamming error logs
    React.useEffect(() => {
      const isDevelopment = import.meta.env.MODE === "development";
      if (!isDevelopment && isSentryEnabledClient()) {
        // Don't capture ExpectedAuthError - these are expected user errors
        if (
          props.error &&
          typeof props.error === "object" &&
          "name" in props.error &&
          props.error.name === "ExpectedAuthError"
        ) {
          return;
        }
        Sentry.captureException(props.error);
      }
    }, [props.error]);

    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootComponent() {
  const { userSession } = useAuthentication();
  const { location } = useRouterState();
  const [analyticsConsent, setAnalyticsConsent] =
    React.useState<AnalyticsConsent | null>(null);
  const [showInitialConsentDialog, setShowInitialConsentDialog] =
    React.useState(false);
  const [showConsentSettingsDialog, setShowConsentSettingsDialog] =
    React.useState(false);
  const [analyticsEnabledInUi, setAnalyticsEnabledInUi] = React.useState(false);

  React.useEffect(() => {
    const consent = readAnalyticsConsent();
    setAnalyticsConsent(consent);
    const analyticsEnabled = consent === "granted";
    setAnalyticsEnabledInUi(analyticsEnabled);
    setShowInitialConsentDialog(consent === null);
  }, []);

  // Set user context in Sentry when user session is available
  // Only set context in production/staging, not during development
  React.useEffect(() => {
    const isDevelopment = import.meta.env.MODE === "development";
    if (isDevelopment) return;
    if (!isSentryEnabledClient()) return;

    if (userSession?.user) {
      Sentry.setUser({
        id: userSession.user.id,
        email: userSession.user.email,
        username:
          userSession.user.name ||
          `${userSession.user.firstName} ${userSession.user.lastName}`,
        // Add additional user data as tags for better filtering
      });

      // Set additional user data as tags
      Sentry.setTag(
        "user.isAdvertiser",
        userSession.user.isAdvertiser ? "true" : "false",
      );
      if (userSession.user.company) {
        Sentry.setTag("user.company", userSession.user.company);
      }
    } else {
      // Clear user context when user logs out
      Sentry.setUser(null);
    }
  }, [userSession]);

  React.useEffect(() => {
    if (!isGtmEnabled || analyticsConsent !== "granted") return;
    loadGtmScript(GTM_ID);
  }, [analyticsConsent]);

  React.useEffect(() => {
    if (!isGtmEnabled || analyticsConsent !== "granted") return;

    pushToDataLayer({
      event: "page_view",
      page_path: `${location.pathname}${location.searchStr || ""}`,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [analyticsConsent, location.pathname, location.searchStr]);

  const saveConsent = React.useCallback((isAnalyticsEnabled: boolean) => {
    const nextConsent: AnalyticsConsent = isAnalyticsEnabled
      ? "granted"
      : "denied";
    saveAnalyticsConsent(nextConsent);
    setAnalyticsConsent(nextConsent);
    setAnalyticsEnabledInUi(isAnalyticsEnabled);
  }, []);

  const handleInitialAcceptAll = React.useCallback(() => {
    saveConsent(true);
    setShowInitialConsentDialog(false);
  }, [saveConsent]);

  const handleInitialSaveSelection = React.useCallback(() => {
    saveConsent(analyticsEnabledInUi);
    setShowInitialConsentDialog(false);
  }, [analyticsEnabledInUi, saveConsent]);

  const handleSaveSettings = React.useCallback(() => {
    saveConsent(analyticsEnabledInUi);
    setShowConsentSettingsDialog(false);
  }, [analyticsEnabledInUi, saveConsent]);

  const handleOpenConsentSettings = React.useCallback(() => {
    setAnalyticsEnabledInUi(analyticsConsent === "granted");
    setShowConsentSettingsDialog(true);
  }, [analyticsConsent]);

  return (
    <RootDocument
      showInitialConsentDialog={showInitialConsentDialog}
      showConsentSettingsDialog={showConsentSettingsDialog}
      analyticsConsent={analyticsConsent}
      analyticsEnabledInUi={analyticsEnabledInUi}
      onAnalyticsEnabledInUiChange={setAnalyticsEnabledInUi}
      onInitialAcceptAll={handleInitialAcceptAll}
      onInitialSaveSelection={handleInitialSaveSelection}
      onOpenConsentSettings={handleOpenConsentSettings}
      onCloseConsentSettings={() => setShowConsentSettingsDialog(false)}
      onSaveConsentSettings={handleSaveSettings}
    >
      <Outlet />
      <ScrollToTopButton />
    </RootDocument>
  );
}

function RootDocument({
  children,
  showInitialConsentDialog = false,
  showConsentSettingsDialog = false,
  analyticsConsent = null,
  analyticsEnabledInUi = false,
  onAnalyticsEnabledInUiChange = () => {},
  onInitialAcceptAll = () => {},
  onInitialSaveSelection = () => {},
  onOpenConsentSettings = () => {},
  onCloseConsentSettings = () => {},
  onSaveConsentSettings = () => {},
}: {
  children: React.ReactNode;
  showInitialConsentDialog?: boolean;
  showConsentSettingsDialog?: boolean;
  analyticsConsent?: AnalyticsConsent | null;
  analyticsEnabledInUi?: boolean;
  onAnalyticsEnabledInUiChange?: (enabled: boolean) => void;
  onInitialAcceptAll?: () => void;
  onInitialSaveSelection?: () => void;
  onOpenConsentSettings?: () => void;
  onCloseConsentSettings?: () => void;
  onSaveConsentSettings?: () => void;
}) {
  // Suppress known SSR streaming errors that don't affect functionality
  // These errors occur during HMR, navigation, or client disconnection in dev mode
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const originalError = console.error;
    const originalWarn = console.warn;

    const shouldSuppress = (message: string): boolean => {
      const lowerMessage = message.toLowerCase();
      return (
        lowerMessage.includes("aborterror") ||
        lowerMessage.includes("this operation was aborted") ||
        lowerMessage.includes("controller is already closed") ||
        lowerMessage.includes("err_invalid_state") ||
        lowerMessage.includes("error reading appstream") ||
        lowerMessage.includes("transformstreamwithrouter")
      );
    };

    console.error = (...args: any[]) => {
      const errorMessage = String(args[0] || "");
      // Suppress known SSR streaming abort errors in development only
      if (shouldSuppress(errorMessage) && import.meta.env.DEV) {
        return; // Silently ignore these errors
      }
      originalError.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      const warnMessage = String(args[0] || "");
      // Also suppress warnings about the same issues
      if (shouldSuppress(warnMessage) && import.meta.env.DEV) {
        return;
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body className="font-[Roboto_Condensed]">
        {children}
        <Dialog
          open={showInitialConsentDialog}
          onOpenChange={(open) => {
            if (!open && analyticsConsent !== null) {
              onCloseConsentSettings();
            }
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="sm:max-w-lg"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>Cookie-Einwilligung</DialogTitle>
              <DialogDescription>
                Wir verwenden erforderliche Cookies für Login und
                Sitzungsverwaltung. Optional kannst du Analytics erlauben, damit
                wir die Nutzung unserer Plattform verbessern.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Erforderliche Cookies</p>
                  <p className="text-muted-foreground text-xs">
                    Immer aktiv (z. B. Login- und Sicherheitscookies)
                  </p>
                </div>
                <Switch checked disabled aria-label="Erforderliche Cookies" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Analytics</p>
                  <p className="text-muted-foreground text-xs">
                    Hilft uns, die Nutzung anonymisiert auszuwerten.
                  </p>
                </div>
                <Switch
                  checked={analyticsEnabledInUi}
                  onCheckedChange={onAnalyticsEnabledInUiChange}
                  aria-label="Analytics erlauben"
                />
              </div>
              <p className="text-muted-foreground text-xs">
                Mehr Informationen findest du in unserer{" "}
                <a href="/de/datenschutz" className="underline">
                  Datenschutzerklärung
                </a>
                .
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onInitialSaveSelection}>
                Auswahl übernehmen
              </Button>
              <Button variant="black" onClick={onInitialAcceptAll}>
                Alle Cookies akzeptieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showConsentSettingsDialog}
          onOpenChange={(open) => {
            if (!open) onCloseConsentSettings();
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Cookie-Einstellungen</DialogTitle>
              <DialogDescription>
                Du kannst deine Einwilligung jederzeit anpassen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>
                Aktueller Status:{" "}
                <span className="font-semibold">
                  {analyticsConsent === "granted"
                    ? "Analytics akzeptiert"
                    : "Nur erforderliche Cookies"}
                </span>
              </p>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Erforderliche Cookies</p>
                  <p className="text-muted-foreground text-xs">
                    Immer aktiv (z. B. Login- und Sicherheitscookies)
                  </p>
                </div>
                <Switch checked disabled aria-label="Erforderliche Cookies" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Analytics</p>
                  <p className="text-muted-foreground text-xs">
                    Hilft uns, die Nutzung anonymisiert auszuwerten.
                  </p>
                </div>
                <Switch
                  checked={analyticsEnabledInUi}
                  onCheckedChange={onAnalyticsEnabledInUiChange}
                  aria-label="Analytics erlauben"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onCloseConsentSettings}>
                Abbrechen
              </Button>
              <Button variant="black" onClick={onSaveConsentSettings}>
                Änderungen speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <button
          type="button"
          className="cursor-pointer fixed bottom-24 left-4 z-90 rounded-full border border-gray-300 bg-white px-1 py-0.5 text-xl text-gray-700 shadow-lg w-10 h-10"
          onClick={onOpenConsentSettings}
          aria-label="Cookie-Einstellungen öffnen"
        >
          🍪
        </button>
        {/* <TanStackRouterDevtools position="top-left" />
        <ReactQueryDevtools buttonPosition="bottom-left" /> */}
        <Toaster theme="dark" position="top-center" />
        <Scripts />
      </body>
    </html>
  );
}
